import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Users, X } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import { adminApi } from "@/lib/api/admin";

// ── Types ────────────────────────────────────────────────────────────

interface UserFilterComboboxProps {
  value: string[];
  onChange: (userIds: string[]) => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

// ── Query key factory ────────────────────────────────────────────────

const adminUserKeys = {
  all: ["admin", "users"] as const,
  list: () => [...adminUserKeys.all, "list"] as const,
};

// ── Component ────────────────────────────────────────────────────────

export function UserFilterCombobox({ value, onChange }: UserFilterComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: adminUserKeys.list(),
    queryFn: adminApi.listAllUsers,
    staleTime: 5 * 60 * 1000,
  });

  const users: UserOption[] = useMemo(() => {
    if (!data) return [];
    return [...data]
      .filter((u) => u.isActive)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((u) => ({ id: u.id, name: u.name, email: u.email }));
  }, [data]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggleUser = useCallback(
    (userId: string) => {
      if (value.length === 0) {
        // Exiting "all users" mode: start an explicit inclusive filter
        onChange([userId]);
      } else if (selectedSet.has(userId)) {
        const next = value.filter((id) => id !== userId);
        onChange(next);
      } else {
        const next = [...value, userId];
        onChange(next);
      }
    },
    [value, selectedSet, onChange],
  );

  const handleShowAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Focus the search input when popover opens
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setSearch("");
    }
  }, [open]);

  // ── Trigger label ────────────────────────────────────────────────

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return "All users";
    return `${value.length} of ${users.length} users`;
  }, [value.length, users.length]);

  const hasActiveFilter = value.length > 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1">
        <Popover.Trigger asChild>
          <Button
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label="Filter by users"
            className={cn(
              "h-9 gap-1.5 text-xs font-medium",
              hasActiveFilter && "border-primary/50 bg-primary/5",
            )}
          >
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{triggerLabel}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </Popover.Trigger>

        {hasActiveFilter ? (
          <button
            type="button"
            aria-label="Clear selected users filter"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            onClick={(e) => {
              e.stopPropagation();
              handleShowAll();
            }}
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          className="z-50 w-[260px] rounded-lg border bg-popover shadow-md outline-none animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              ref={inputRef}
              placeholder="Search users…"
              value={search}
              onValueChange={setSearch}
            />

            {/* Show All / Clear Filter actions */}
            <div className="flex items-center justify-end border-b px-3 py-1.5">
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleShowAll}
                disabled={value.length === 0}
              >
                Clear filter
              </button>
            </div>

            <CommandList>
              <CommandEmpty>
                {isLoading ? "Loading users…" : "No users found."}
              </CommandEmpty>
              <CommandGroup>
                {users
                  .filter((u) => {
                    if (!search) return true;
                    const q = search.toLowerCase();
                    return (
                      u.name.toLowerCase().includes(q) ||
                      u.email.toLowerCase().includes(q)
                    );
                  })
                  .map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => toggleUser(user.id)}
                      className="gap-2"
                    >
                      <Checkbox
                        checked={selectedSet.has(user.id)}
                        aria-hidden="true"
                        tabIndex={-1}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate text-sm">{user.name}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
